import React, { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  TextInput,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { CameraIcon, ExclamationCircleIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useVmSnapshots } from '../../../hooks/useVmSnapshots';
import { useNotifications } from '../../../contexts/NotificationContext';

interface VmLiveSnapshotsSectionProps {
  vmId: number;
  vmName: string;
}

/**
 * Live hypervisor snapshots — mutable and short-lived, created and deleted from here.
 *
 * This is the original content of the Snapshots tab, extracted unchanged so that its loading,
 * error and "none yet" states replace **this section only**. They used to `return` early from the
 * whole tab, which meant the backups section below could never render for a VM with no snapshots
 * — the common case.
 */
const VmLiveSnapshotsSection: React.FunctionComponent<VmLiveSnapshotsSectionProps> = ({ vmId, vmName }) => {
  const { snapshots, isLoading, error, refetch, createSnapshot, deleteSnapshot } = useVmSnapshots(vmId);
  const { addNotification, setDrawerOpen } = useNotifications();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newSnapshotName, setNewSnapshotName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const [deleteSnapshotId, setDeleteSnapshotId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCreate = async () => {
    if (!newSnapshotName.trim()) return;
    setIsCreating(true);
    try {
      await createSnapshot(vmId, newSnapshotName.trim());
      addNotification({
        title: 'Snapshot created',
        srTitle: 'Success alert',
        description: `Snapshot "${newSnapshotName.trim()}" created for ${vmName}.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      setIsCreateModalOpen(false);
      setNewSnapshotName('');
    } catch (err) {
      addNotification({
        title: 'Snapshot creation failed',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async () => {
    if (deleteSnapshotId === null) return;
    setIsDeleting(true);
    try {
      await deleteSnapshot(vmId, deleteSnapshotId);
      addNotification({
        title: 'Snapshot deleted',
        srTitle: 'Success alert',
        description: `Snapshot deleted successfully.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      setDeleteSnapshotId(null);
    } catch (err) {
      addNotification({
        title: 'Snapshot deletion failed',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load snapshots"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error.message}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (snapshots.length === 0) {
      return (
        <EmptyState titleText="No snapshots" icon={CameraIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>No snapshots have been created for this virtual machine.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
                Create Snapshot
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Button variant="primary" onClick={() => setIsCreateModalOpen(true)} style={{ marginBottom: '1rem' }}>
          Create Snapshot
        </Button>

        <Table aria-label="Snapshots list" variant="compact">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Created At</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {snapshots.map((snapshot) => (
              <Tr key={snapshot.id}>
                <Td dataLabel="Name">{snapshot.name}</Td>
                <Td dataLabel="Created At">{snapshot.created_at}</Td>
                <Td dataLabel="Actions">
                  <Button variant="danger" size="sm" onClick={() => setDeleteSnapshotId(snapshot.id)}>
                    Delete
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </>
    );
  };

  return (
    <Card isCompact>
      <CardTitle>Snapshots</CardTitle>
      <CardBody>
        <Content component="p">
          Point-in-time snapshots held on the hypervisor alongside the running VM. Short-lived, and yours to create
          and delete.
        </Content>

        {renderBody()}

        {/* Rendered once, outside the body, so the create modal stays reachable from both the
            table's button and the empty state's call to action. */}
        <Modal
          variant={ModalVariant.small}
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setNewSnapshotName('');
          }}
          aria-label="Create snapshot"
        >
          <ModalHeader title="Create Snapshot" />
          <ModalBody>
            <TextInput
              value={newSnapshotName}
              onChange={(_event, value) => setNewSnapshotName(value)}
              aria-label="Snapshot name"
              placeholder="Enter snapshot name"
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="primary"
              onClick={handleCreate}
              isLoading={isCreating}
              isDisabled={isCreating || !newSnapshotName.trim()}
            >
              Create
            </Button>
            <Button
              variant="link"
              onClick={() => {
                setIsCreateModalOpen(false);
                setNewSnapshotName('');
              }}
              isDisabled={isCreating}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        {deleteSnapshotId !== null && (
          <Modal
            variant={ModalVariant.small}
            isOpen={deleteSnapshotId !== null}
            onClose={() => setDeleteSnapshotId(null)}
            aria-label="Confirm delete snapshot"
          >
            <ModalHeader title="Delete Snapshot" titleIconVariant="warning" />
            <ModalBody>Are you sure you want to delete this snapshot?</ModalBody>
            <ModalFooter>
              <Button variant="danger" onClick={handleDelete} isLoading={isDeleting} isDisabled={isDeleting}>
                Delete
              </Button>
              <Button variant="link" onClick={() => setDeleteSnapshotId(null)} isDisabled={isDeleting}>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>
        )}
      </CardBody>
    </Card>
  );
};

export default VmLiveSnapshotsSection;
